<template>
    <div v-show="visible" class="pp-dialog__wrap" :class="customClass">
        <div v-if="layer" class="pp-dialog__layer"></div>
        <div class="pp-dialog">
            <div class="pp-dialog__title">
                <slot name="title">{{title}}</slot>
            </div>
            <div class="pp-dialog__body">
                <div v-if="!innerHTML" class="pp-dialog__content">
                    <slot>
                        <template v-if="type === 'prompt'">
                            <pFormItem :error="error" size="normal">
                                <pInput v-focus v-model="currentInputValue"
                                    :type="inputType"
                                    :placeholder="inputPlaceholder"
                                    @change="inputChange"
                                ></pInput>
                            </pFormItem>
                        </template>
                        <template v-else>
                            <i v-if="iconClass"
                                :class="['pp-dialog__icon', iconClass]"></i>
                            <span>{{message}}</span>
                        </template>
                    </slot>
                </div>
                <div v-else class="pp-dialog__content" v-html="innerHTML"></div>
            </div>
            <div class="pp-dialog__footer">
                <span v-if="showCancelButton" class="pp-dialog__button-cancel"
                    @click="clickCancel">
                    {{cancelButtonText}}
                </span>
                <span class="pp-dialog__button-confirm" @click="clickConfirm">
                    <i v-if="confirmButtonLoading"
                        class="pp-icon-loading"
                    ></i>
                    {{confirmButtonText}}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import pInput from '../../input'
import pFormItem from '../../form-item'

/**
 * 对话框
 * @param {String} customClass 对话框样式类
 * @param {String} type ['confirm', 'prompt']
 * @param {String} title 标题
 * @param {[String, Function]} message 提示消息(function 后期)
 * @param {String} innerHTML 消息的html
 * @param {String} iconClass 图标
 * @param {Function} confirm 点击确认的回调(type=prompt时，回调参数为input输入值)
 * @param {String} confirmButtonText 确认按钮的文字
 * @param {Function} cancel 点击取消的回调
 * @param {String} cancelButtonText 取消按钮的文字
 * @param {Boolean} showCancelButton 显示取消按钮 default：false
 * @param {Boolean} layer 遮罩层 default: false
 * @param {[String, Number]} inputValue type=prompt时input的初始值
 * @param {String} inputPlaceholder type=prompt时输入框占位符
 * @param {String} inputErrorMessage 校验失败时的提示文字
 * @param {String} inputType type=prompt时输入框类型
 * @param {[RegExp, String]} inputPattern type=prompt时输入值的校验规则
 * @param {Function} beforeClose (action, instance, done)=> {} 关闭前回调 action: confirm|cancel, instance:组件实例, done()关闭方法
 * @param {Boolean} closeOnHashChange 在hash变更时销毁组件
 */
export default {
    name: 'pp-dialog',
    components: {
        pInput, pFormItem,
    },
    props: {
        customClass: String,
        type: {
            type: String,
            default: '',
        },
        title: String,
        message: [String, Function],
        innerHTML: String,
        iconClass: String,
        confirm: {
            type: Function,
            default: function() {},
        },
        confirmButtonText: {
            type: String,
            default: '确认',
        },
        cancel: {
            type: Function,
            default: function() {},
        },
        cancelButtonText: {
            type: String,
            default: '取消',
        },
        showCancelButton: Boolean,
        layer: Boolean,
        inputValue: {
            type: [String, Number],
            default: '',
        },
        inputPlaceholder: String,
        inputErrorMessage: {
            type: String,
            default: '请输入合法数据'
        },
        inputType: String,
        inputPattern: [RegExp, String],
        beforeClose: Function,
        closeOnHashChange: {
            type: Boolean,
            default: true,
        }
    },
    data: function() {
        return {
            visible: false,
            currentInputValue: '',
            error: '',
            confirmButtonLoading: false,
        };
    },
    mounted: function() {
        this.currentInputValue = this.inputValue
        this.$nextTick(() => {
            if (this.closeOnHashChange) {
                window.addEventListener('hashchange', this.close);
            }
        });
    },
    beforeDestroy() {
        if(this.closeOnHashChange) {
            window.removeEventListener('hashchange', this.close);
        }
        setTimeout(() => {
            this.close();
        });
    },
    methods: {
        done: function() {
            this.visible = false
            this.$destroy(true)
            this.$el.parentNode && this.$el.parentNode.removeChild(this.$el)
        },

        close: function(action) {
            if(this.beforeClose) {
                this.beforeClose(action, this, this.done)
            }
            else {
                this.done()
            }
        },

        clickCancel: function() {
            this.$emit('cancel')
            this.close('cancel')
        },

        clickConfirm: function() {
            if(this.confirmButtonLoading) return;

            this.validate().then(()=> {
                this.confirm({
                    value: this.currentInputValue
                })
                this.error = ''
                this.$emit('confirm')
                this.close('confirm')
            }).catch(err=> {
                this.error = err
            })
        },

        inputChange: function() {
            this.validate().then(()=> {
                this.error = ''
            }).catch(err=> {
                this.error = err
            })
        },

        validate: function() {
            return new Promise((resolve, reject)=> {
                if(this.type === 'prompt' && this.inputPattern) {
                    this.inputPattern.test(this.currentInputValue)
                        ? resolve(true)
                        : reject(this.inputErrorMessage)
                }
                else {
                    resolve(true)
                }
            });
        },
    },

}
</script>
